import React, { useEffect, useState, useContext, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  Button,
  Typography,
  makeStyles,
  CircularProgress,
  Tooltip,
  IconButton,
  Switch
} from "@material-ui/core";
import {
  Close as CloseIcon,
  Check as CheckIcon,
  HelpOutline as HelpOutlineIcon,
  Star as StarIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  Contacts as ContactsIcon,
  FlashOn as FlashOnIcon,
  LocalOffer as LocalOfferIcon,
  Chat as ChatIcon,
  FilterList as FilterListIcon,
  AccountTree as AccountTreeIcon,
  WhatsApp as WhatsAppIcon,
  Telegram as TelegramIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  Email as EmailIcon,
  Memory as MemoryIcon,
  VpnKey as VpnKeyIcon,
  TouchApp as TouchAppIcon,
  FormatListBulleted as ListIcon,
  ViewCarousel as CarouselIcon,
  Poll as PollIcon,
  ViewColumn as KanbanIcon,
  Event as EventIcon,
  Announcement as AnnouncementIcon,
  Assignment as AssignmentIcon,
  DynamicFeed as DynamicFeedIcon,
  GroupWork as GroupWorkIcon,
  Notifications as BellIcon,
  Widgets as WidgetsIcon,
  Language as LanguageIcon,
  HeadsetMic as HeadsetIcon,
  Tune as TuneIcon,
  Share as ShareIcon,
  Send as SendIcon,
  Build as BuildIcon,
  MoreHoriz as MoreHorizIcon
} from "@material-ui/icons";
import usePlans from "../../hooks/usePlans";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import useSettings from "../../hooks/useSettings";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";
  return {
    dialogPaper: {
      borderRadius: 16,
      backgroundColor: isDark ? "#111418" : "#ffffff",
      color: isDark ? "#ffffff" : "#1e293b",
      padding: 0,
      overflow: "hidden",
      maxWidth: "1560px !important",
      width: "96vw",
      margin: "0 auto",
    },
    headerBox: {
      textAlign: "center",
      padding: "26px 20px 10px 20px",
      position: "relative",
    },
    closeButton: {
      position: "absolute",
      right: 16,
      top: 16,
      color: isDark ? "#94a3b8" : "#64748b",
    },
    pillBadge: {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      padding: "6px 18px",
      borderRadius: 30,
      backgroundColor: isDark ? "rgba(56, 75, 255, 0.15)" : "#eff2fe",
      color: "#384bff",
      fontWeight: 800,
      fontSize: "0.82rem",
      letterSpacing: "1.5px",
      textTransform: "uppercase",
      marginBottom: 10,
    },
    mainTitle: {
      fontSize: "2rem",
      fontWeight: 800,
      color: isDark ? "#ffffff" : "#161b33",
      marginBottom: 8,
    },
    subtitleText: {
      fontSize: "0.95rem",
      color: isDark ? "#94a3b8" : "#64748b",
      maxWidth: 620,
      margin: "0 auto",
    },
    switcherContainer: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 12,
      margin: "20px 0 15px 0",
    },
    switcherLabel: {
      fontSize: "0.95rem",
      fontWeight: 700,
      cursor: "pointer",
      userSelect: "none",
      color: isDark ? "#94a3b8" : "#64748b",
      transition: "color 0.2s ease",
    },
    switcherLabelActive: {
      color: "#384bff !important",
      fontWeight: 800,
    },
    discountBadge: {
      backgroundColor: "#e0f2fe",
      color: "#0284c7",
      fontSize: "0.75rem",
      fontWeight: 800,
      padding: "2px 8px",
      borderRadius: 12,
      border: "1px solid #bae6fd",
      marginLeft: 6,
    },
    tableContainer: {
      overflowX: "auto",
      padding: "10px 24px 30px 24px",
      display: "flex",
      justifyContent: "center",
      ...theme.scrollbarStyles,
    },
    table: {
      width: "100%",
      maxWidth: 1500,
      margin: "0 auto",
      borderCollapse: "separate",
      borderSpacing: "12px 0",
      tableLayout: "fixed",
    },
    // Column Header styles
    thFeat: {
      verticalAlign: "middle",
      border: isDark ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid #e2e8f0",
      borderBottom: isDark ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid #e2e8f0",
      borderRadius: "16px 16px 0 0",
      padding: "24px 16px",
      backgroundColor: isDark ? "#1a1f26" : "#f8faff",
      textAlign: "center",
    },
    thPlan: {
      verticalAlign: "top",
      borderRadius: "16px 16px 0 0",
      padding: "24px 14px",
      position: "relative",
      textAlign: "center",
      overflow: "hidden",
    },
    thFree: {
      border: "2px solid #fbbf24",
      borderBottom: "2px solid #fbbf24",
      backgroundColor: isDark ? "rgba(251, 191, 36, 0.08)" : "#fffbe6",
    },
    thBasic: {
      border: "2px solid #384bff",
      borderBottom: "2px solid #384bff",
      backgroundColor: isDark ? "rgba(56, 75, 255, 0.08)" : "#e0f2fe",
    },
    thAdvance: {
      border: "2px solid #22c55e",
      borderBottom: "2px solid #22c55e",
      backgroundColor: isDark ? "rgba(34, 197, 94, 0.08)" : "#dcfce7",
    },
    thPremium: {
      border: "2px solid #f43f5e",
      borderBottom: "2px solid #f43f5e",
      backgroundColor: isDark ? "rgba(244, 63, 94, 0.08)" : "#ffe4e6",
    },
    popularBadge: {
      position: "absolute",
      top: 21,
      right: -52,
      width: 170,
      backgroundColor: "#ff3333",
      color: "#ffffff",
      textAlign: "center",
      padding: "4px 0",
      transform: "rotate(45deg)",
      fontSize: "11px",
      fontWeight: 700,
      zIndex: 2,
      letterSpacing: "0.5px",
      boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
      whiteSpace: "nowrap",
      pointerEvents: "none",
    },
    currentPlanBadge: {
      display: "inline-block",
      backgroundColor: "#10b981",
      color: "#ffffff",
      fontSize: "0.75rem",
      fontWeight: 800,
      padding: "4px 12px",
      borderRadius: 12,
      marginBottom: 8,
      letterSpacing: "0.5px",
    },
    planTitle: {
      fontWeight: 800,
      fontSize: "1.35rem",
      color: isDark ? "#ffffff" : "#161b33",
      marginBottom: 6,
    },
    planPrice: {
      fontSize: "1.5rem",
      fontWeight: 800,
      color: isDark ? "#ffffff" : "#161b33",
      whiteSpace: "nowrap",
    },
    planPeriod: {
      fontSize: "0.85rem",
      fontWeight: 500,
      color: isDark ? "#94a3b8" : "#64748b",
      marginLeft: 4,
    },
    planSubtitle: {
      color: isDark ? "#94a3b8" : "#555555",
      fontSize: "0.82rem",
      marginTop: 4,
      marginBottom: 12,
      minHeight: 20,
    },
    // Category Row
    catRowFeat: {
      borderLeft: isDark ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid #e2e8f0",
      borderRight: isDark ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid #e2e8f0",
      backgroundColor: isDark ? "#1a1f26" : "#f8faff",
      padding: "12px 6px 6px 6px",
      textAlign: "center",
    },
    catLabel: {
      display: "flex",
      width: "100%",
      boxSizing: "border-box",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      padding: "6px 10px",
      borderRadius: 8,
      fontWeight: 700,
      fontSize: "0.82rem",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    },
    catEmptyFree: {
      borderLeft: "2px solid #fbbf24",
      borderRight: "2px solid #fbbf24",
      backgroundColor: isDark ? "#16191E" : "#ffffff",
    },
    catEmptyBasic: {
      borderLeft: "2px solid #384bff",
      borderRight: "2px solid #384bff",
      backgroundColor: isDark ? "#16191E" : "#ffffff",
    },
    catEmptyAdv: {
      borderLeft: "2px solid #22c55e",
      borderRight: "2px solid #22c55e",
      backgroundColor: isDark ? "#16191E" : "#ffffff",
    },
    catEmptyPrem: {
      borderLeft: "2px solid #f43f5e",
      borderRight: "2px solid #f43f5e",
      backgroundColor: isDark ? "#16191E" : "#ffffff",
    },
    // Cell styles
    tdFeat: {
      borderLeft: isDark ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid #e2e8f0",
      borderRight: isDark ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid #e2e8f0",
      backgroundColor: isDark ? "#1a1f26" : "#f8faff",
      padding: "9px 16px",
      fontSize: "0.85rem",
      fontWeight: 600,
      color: isDark ? "#e2e8f0" : "#334155",
      textAlign: "left",
      display: "flex",
      alignItems: "center",
      overflow: "hidden",
    },
    tdFree: {
      borderLeft: "2px solid #fbbf24",
      borderRight: "2px solid #fbbf24",
      backgroundColor: isDark ? "#16191E" : "#ffffff",
      padding: "9px 12px",
      fontSize: "0.85rem",
      textAlign: "center",
      color: isDark ? "#e2e8f0" : "#334155",
    },
    tdBasic: {
      borderLeft: "2px solid #384bff",
      borderRight: "2px solid #384bff",
      backgroundColor: isDark ? "#16191E" : "#ffffff",
      padding: "9px 12px",
      fontSize: "0.85rem",
      textAlign: "center",
      color: isDark ? "#e2e8f0" : "#334155",
    },
    tdAdv: {
      borderLeft: "2px solid #22c55e",
      borderRight: "2px solid #22c55e",
      backgroundColor: isDark ? "#16191E" : "#ffffff",
      padding: "9px 12px",
      fontSize: "0.85rem",
      textAlign: "center",
      color: isDark ? "#e2e8f0" : "#334155",
    },
    tdPrem: {
      borderLeft: "2px solid #f43f5e",
      borderRight: "2px solid #f43f5e",
      backgroundColor: isDark ? "#16191E" : "#ffffff",
      padding: "9px 12px",
      fontSize: "0.85rem",
      textAlign: "center",
      color: isDark ? "#e2e8f0" : "#334155",
    },
    // Bottom row with radius
    tdFeatBottom: {
      borderLeft: isDark ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid #e2e8f0",
      borderRight: isDark ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid #e2e8f0",
      borderBottom: isDark ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid #e2e8f0",
      borderRadius: "0 0 16px 16px",
      backgroundColor: isDark ? "#1a1f26" : "#f8faff",
      padding: "20px 24px",
    },
    tdFreeBottom: {
      borderLeft: "2px solid #fbbf24",
      borderRight: "2px solid #fbbf24",
      borderBottom: "2px solid #fbbf24",
      borderRadius: "0 0 16px 16px",
      backgroundColor: isDark ? "#16191E" : "#ffffff",
      padding: "20px 14px",
      textAlign: "center",
    },
    tdBasicBottom: {
      borderLeft: "2px solid #384bff",
      borderRight: "2px solid #384bff",
      borderBottom: "2px solid #384bff",
      borderRadius: "0 0 16px 16px",
      backgroundColor: isDark ? "#16191E" : "#ffffff",
      padding: "20px 14px",
      textAlign: "center",
    },
    tdAdvBottom: {
      borderLeft: "2px solid #22c55e",
      borderRight: "2px solid #22c55e",
      borderBottom: "2px solid #22c55e",
      borderRadius: "0 0 16px 16px",
      backgroundColor: isDark ? "#16191E" : "#ffffff",
      padding: "20px 14px",
      textAlign: "center",
    },
    tdPremBottom: {
      borderLeft: "2px solid #f43f5e",
      borderRight: "2px solid #f43f5e",
      borderBottom: "2px solid #f43f5e",
      borderRadius: "0 0 16px 16px",
      backgroundColor: isDark ? "#16191E" : "#ffffff",
      padding: "20px 14px",
      textAlign: "center",
    },
    // Button styles
    btnBasic: {
      backgroundColor: "#384bff",
      color: "#ffffff",
      fontWeight: 700,
      borderRadius: 10,
      padding: "8px 16px",
      textTransform: "none",
      fontSize: "0.88rem",
      "&:hover": {
        backgroundColor: "#2c3ec7",
      },
    },
    btnAdvance: {
      backgroundColor: "#22c55e",
      color: "#ffffff",
      fontWeight: 700,
      borderRadius: 10,
      padding: "8px 16px",
      textTransform: "none",
      fontSize: "0.88rem",
      "&:hover": {
        backgroundColor: "#16a34a",
      },
    },
    btnPremium: {
      backgroundColor: "#f43f5e",
      color: "#ffffff",
      fontWeight: 700,
      borderRadius: 10,
      padding: "8px 16px",
      textTransform: "none",
      fontSize: "0.88rem",
      "&:hover": {
        backgroundColor: "#e11d48",
      },
    },
    btnCurrent: {
      borderColor: "#10b981",
      color: "#10b981",
      fontWeight: 700,
      borderRadius: 10,
      padding: "8px 16px",
      textTransform: "none",
      fontSize: "0.88rem",
      borderWidth: 2,
    },
    btnDisabled: {
      backgroundColor: isDark ? "rgba(255, 255, 255, 0.08) !important" : "#e2e8f0 !important",
      color: isDark ? "#64748b !important" : "#94a3b8 !important",
      fontWeight: 700,
      borderRadius: 10,
      padding: "8px 16px",
      textTransform: "none",
      fontSize: "0.88rem",
      cursor: "not-allowed !important",
    },
    valYes: {
      color: "#10b981",
      fontWeight: 700,
    },
    valNo: {
      color: isDark ? "#64748b" : "#94a3b8",
      fontWeight: 500,
    },
    valUnlimited: {
      color: "#384bff",
      fontWeight: 700,
    },
    valNum: {
      fontWeight: 700,
    },
  };
});

const getCategoryIcon = (iconKey) => {
  const iconProps = { style: { fontSize: "1rem", marginRight: 6 } };
  switch (iconKey) {
    case "users": return <GroupIcon {...iconProps} />;
    case "limits": return <TuneIcon {...iconProps} />;
    case "channels": return <ShareIcon {...iconProps} />;
    case "ai": return <MemoryIcon {...iconProps} />;
    case "interactive": return <TouchAppIcon {...iconProps} />;
    case "crm": return <KanbanIcon {...iconProps} />;
    case "posts": return <SendIcon {...iconProps} />;
    case "operations": return <BuildIcon {...iconProps} />;
    case "other": return <MoreHorizIcon {...iconProps} />;
    default: return null;
  }
};

const getFeatureIcon = (iconKey, color) => {
  const iconProps = {
    style: {
      color: color || "#384bff",
      fontSize: "1.15rem",
      marginRight: 10,
      flexShrink: 0
    }
  };
  switch (iconKey) {
    case "person": return <PersonIcon {...iconProps} />;
    case "group": return <GroupIcon {...iconProps} />;
    case "contacts": return <ContactsIcon {...iconProps} />;
    case "flash": return <FlashOnIcon {...iconProps} />;
    case "tag": return <LocalOfferIcon {...iconProps} />;
    case "chat": return <ChatIcon {...iconProps} />;
    case "filter": return <FilterListIcon {...iconProps} />;
    case "tree": return <AccountTreeIcon {...iconProps} />;
    case "whatsapp": return <WhatsAppIcon {...iconProps} />;
    case "telegram": return <TelegramIcon {...iconProps} />;
    case "facebook": return <FacebookIcon {...iconProps} />;
    case "instagram": return <InstagramIcon {...iconProps} />;
    case "email": return <EmailIcon {...iconProps} />;
    case "ai": return <MemoryIcon {...iconProps} />;
    case "key": return <VpnKeyIcon {...iconProps} />;
    case "touch": return <TouchAppIcon {...iconProps} />;
    case "list": return <ListIcon {...iconProps} />;
    case "carousel": return <CarouselIcon {...iconProps} />;
    case "poll": return <PollIcon {...iconProps} />;
    case "kanban": return <KanbanIcon {...iconProps} />;
    case "event": return <EventIcon {...iconProps} />;
    case "campaign": return <AnnouncementIcon {...iconProps} />;
    case "form": return <AssignmentIcon {...iconProps} />;
    case "status": return <DynamicFeedIcon {...iconProps} />;
    case "community": return <GroupWorkIcon {...iconProps} />;
    case "bell": return <BellIcon {...iconProps} />;
    case "widget": return <WidgetsIcon {...iconProps} />;
    case "lang": return <LanguageIcon {...iconProps} />;
    case "headset": return <HeadsetIcon {...iconProps} />;
    default: return <CheckIcon {...iconProps} />;
  }
};

const getMatrixRows = () => [
  {
    type: "category",
    name: i18n.t("finance.planSelector.categories.users"),
    iconKey: "users",
    color: "#0ea5e9",
    bgColor: "#e0f2fe",
  },
  {
    name: i18n.t("finance.planSelector.feats.admin"),
    iconKey: "person",
    iconColor: "#fbbf24",
    free: i18n.t("finance.planSelector.values.yes"),
    basic: i18n.t("finance.planSelector.values.yes"),
    advance: i18n.t("finance.planSelector.values.yes"),
    premium: i18n.t("finance.planSelector.values.yes"),
  },
  {
    name: i18n.t("finance.planSelector.feats.salesAgents"),
    iconKey: "group",
    iconColor: "#fbbf24",
    free: i18n.t("finance.planSelector.values.no"),
    basic: i18n.t("finance.planSelector.values.agents", { count: 2 }),
    advance: i18n.t("finance.planSelector.values.agents", { count: 5 }),
    premium: i18n.t("finance.planSelector.values.agents", { count: 10 }),
  },
  {
    type: "category",
    name: i18n.t("finance.planSelector.categories.limits"),
    iconKey: "limits",
    color: "#f59e0b",
    bgColor: "#fef3c7",
  },
  {
    name: i18n.t("finance.planSelector.feats.contacts"),
    iconKey: "contacts",
    iconColor: "#6366f1",
    free: i18n.t("finance.planSelector.values.contacts", { count: 200 }),
    basic: i18n.t("finance.planSelector.values.unlimited"),
    advance: i18n.t("finance.planSelector.values.unlimited"),
    premium: i18n.t("finance.planSelector.values.unlimited"),
  },
  {
    name: i18n.t("finance.planSelector.feats.automations"),
    iconKey: "flash",
    iconColor: "#f59e0b",
    free: i18n.t("finance.planSelector.values.contacts", { count: 200 }),
    basic: i18n.t("finance.planSelector.values.contacts", { count: "5,000" }),
    advance: i18n.t("finance.planSelector.values.contacts", { count: "20,000" }),
    premium: i18n.t("finance.planSelector.values.contacts", { count: "60,000" }),
  },
  {
    name: i18n.t("finance.planSelector.feats.brands"),
    iconKey: "tag",
    iconColor: "#ec4899",
    free: i18n.t("finance.planSelector.values.brand", { count: 1 }),
    basic: i18n.t("finance.planSelector.values.brand", { count: 1 }),
    advance: i18n.t("finance.planSelector.values.brands", { count: 3 }),
    premium: i18n.t("finance.planSelector.values.brands", { count: 10 }),
  },
  {
    name: i18n.t("finance.planSelector.feats.messages"),
    iconKey: "chat",
    iconColor: "#0ea5e9",
    free: "2,000",
    basic: i18n.t("finance.planSelector.values.unlimited"),
    advance: i18n.t("finance.planSelector.values.unlimited"),
    premium: i18n.t("finance.planSelector.values.unlimited"),
  },
  {
    name: i18n.t("finance.planSelector.feats.funnels"),
    iconKey: "filter",
    iconColor: "#8b5cf6",
    free: "1",
    basic: "5",
    advance: "25",
    premium: "250",
  },
  {
    name: i18n.t("finance.planSelector.feats.flows"),
    iconKey: "tree",
    iconColor: "#10b981",
    free: "5",
    basic: "50",
    advance: "250",
    premium: "2,500",
  },
  {
    type: "category",
    name: i18n.t("finance.planSelector.categories.channels"),
    iconKey: "channels",
    color: "#22c55e",
    bgColor: "#dcfce7",
  },
  {
    name: i18n.t("finance.planSelector.feats.waQr"),
    iconKey: "whatsapp",
    iconColor: "#22c55e",
    free: i18n.t("finance.planSelector.values.connection", { count: 1 }),
    basic: i18n.t("finance.planSelector.values.connection", { count: 1 }),
    advance: i18n.t("finance.planSelector.values.connections", { count: 3 }),
    premium: i18n.t("finance.planSelector.values.connections", { count: 6 }),
  },
  {
    name: i18n.t("finance.planSelector.feats.waOfficial"),
    iconKey: "whatsapp",
    iconColor: "#128c7e",
    free: i18n.t("finance.planSelector.values.no"),
    basic: i18n.t("finance.planSelector.values.connection", { count: 1 }),
    advance: i18n.t("finance.planSelector.values.connections", { count: 3 }),
    premium: i18n.t("finance.planSelector.values.connections", { count: 6 }),
  },
  {
    name: i18n.t("finance.planSelector.feats.telegram"),
    iconKey: "telegram",
    iconColor: "#0088cc",
    free: i18n.t("finance.planSelector.values.no"),
    basic: i18n.t("finance.planSelector.values.connection", { count: 1 }),
    advance: i18n.t("finance.planSelector.values.connections", { count: 3 }),
    premium: i18n.t("finance.planSelector.values.connections", { count: 6 }),
  },
  {
    name: i18n.t("finance.planSelector.feats.messenger"),
    iconKey: "facebook",
    iconColor: "#1877f2",
    free: i18n.t("finance.planSelector.values.no"),
    basic: i18n.t("finance.planSelector.values.connection", { count: 1 }),
    advance: i18n.t("finance.planSelector.values.connections", { count: 3 }),
    premium: i18n.t("finance.planSelector.values.connections", { count: 6 }),
  },
  {
    name: i18n.t("finance.planSelector.feats.instagram"),
    iconKey: "instagram",
    iconColor: "#e1306c",
    free: i18n.t("finance.planSelector.values.no"),
    basic: i18n.t("finance.planSelector.values.connection", { count: 1 }),
    advance: i18n.t("finance.planSelector.values.connections", { count: 3 }),
    premium: i18n.t("finance.planSelector.values.connections", { count: 6 }),
  },
  {
    name: i18n.t("finance.planSelector.feats.email"),
    iconKey: "email",
    iconColor: "#795548",
    free: i18n.t("finance.planSelector.values.no"),
    basic: i18n.t("finance.planSelector.values.connection", { count: 1 }),
    advance: i18n.t("finance.planSelector.values.connections", { count: 3 }),
    premium: i18n.t("finance.planSelector.values.connections", { count: 6 }),
  },
  {
    type: "category",
    name: i18n.t("finance.planSelector.categories.ai"),
    iconKey: "ai",
    color: "#a855f7",
    bgColor: "#f3e8ff",
  },
  {
    name: i18n.t("finance.planSelector.feats.aiAgent"),
    iconKey: "ai",
    iconColor: "#ef4444",
    tooltip: i18n.t("finance.planSelector.tooltips.aiCreditsRequired"),
    free: `${i18n.t("finance.planSelector.values.included")} *`,
    basic: `${i18n.t("finance.planSelector.values.unlimited")} *`,
    advance: `${i18n.t("finance.planSelector.values.unlimited")} *`,
    premium: `${i18n.t("finance.planSelector.values.unlimited")} *`,
  },
  {
    name: i18n.t("finance.planSelector.feats.ragAssistant"),
    iconKey: "ai",
    iconColor: "#a855f7",
    tooltip: i18n.t("finance.planSelector.tooltips.aiCreditsRequired"),
    free: `${i18n.t("finance.planSelector.values.included")} *`,
    basic: `${i18n.t("finance.planSelector.values.unlimited")} *`,
    advance: `${i18n.t("finance.planSelector.values.unlimited")} *`,
    premium: `${i18n.t("finance.planSelector.values.unlimited")} *`,
  },
  {
    name: i18n.t("finance.planSelector.feats.openaiPersonal"),
    iconKey: "key",
    iconColor: "#10b981",
    free: i18n.t("finance.planSelector.values.included"),
    basic: i18n.t("finance.planSelector.values.unlimited"),
    advance: i18n.t("finance.planSelector.values.unlimited"),
    premium: i18n.t("finance.planSelector.values.unlimited"),
  },
  {
    type: "category",
    name: i18n.t("finance.planSelector.categories.interactive"),
    iconKey: "interactive",
    color: "#be185d",
    bgColor: "#fce7f3",
  },
  {
    name: i18n.t("finance.planSelector.feats.buttons"),
    iconKey: "touch",
    iconColor: "#be185d",
    free: i18n.t("finance.planSelector.values.included"),
    basic: i18n.t("finance.planSelector.values.unlimited"),
    advance: i18n.t("finance.planSelector.values.unlimited"),
    premium: i18n.t("finance.planSelector.values.unlimited"),
  },
  {
    name: i18n.t("finance.planSelector.feats.lists"),
    iconKey: "list",
    iconColor: "#3b82f6",
    free: i18n.t("finance.planSelector.values.included"),
    basic: i18n.t("finance.planSelector.values.unlimited"),
    advance: i18n.t("finance.planSelector.values.unlimited"),
    premium: i18n.t("finance.planSelector.values.unlimited"),
  },
  {
    name: i18n.t("finance.planSelector.feats.carousels"),
    iconKey: "carousel",
    iconColor: "#10b981",
    free: i18n.t("finance.planSelector.values.included"),
    basic: i18n.t("finance.planSelector.values.unlimited"),
    advance: i18n.t("finance.planSelector.values.unlimited"),
    premium: i18n.t("finance.planSelector.values.unlimited"),
  },
  {
    name: i18n.t("finance.planSelector.feats.surveys"),
    iconKey: "poll",
    iconColor: "#f59e0b",
    free: i18n.t("finance.planSelector.values.included"),
    basic: i18n.t("finance.planSelector.values.unlimited"),
    advance: i18n.t("finance.planSelector.values.unlimited"),
    premium: i18n.t("finance.planSelector.values.unlimited"),
  },
  {
    type: "category",
    name: i18n.t("finance.planSelector.categories.crm"),
    iconKey: "crm",
    color: "#f97316",
    bgColor: "#ffedd5",
  },
  {
    name: i18n.t("finance.planSelector.feats.crmKanban"),
    iconKey: "kanban",
    iconColor: "#f97316",
    free: i18n.t("finance.planSelector.values.included"),
    basic: i18n.t("finance.planSelector.values.unlimited"),
    advance: i18n.t("finance.planSelector.values.unlimited"),
    premium: i18n.t("finance.planSelector.values.unlimited"),
  },
  {
    name: i18n.t("finance.planSelector.feats.appointments"),
    iconKey: "event",
    iconColor: "#6366f1",
    free: i18n.t("finance.planSelector.values.included"),
    basic: i18n.t("finance.planSelector.values.unlimited"),
    advance: i18n.t("finance.planSelector.values.unlimited"),
    premium: i18n.t("finance.planSelector.values.unlimited"),
  },
  {
    name: i18n.t("finance.planSelector.feats.remarketing"),
    iconKey: "campaign",
    iconColor: "#f59e0b",
    free: i18n.t("finance.planSelector.values.included"),
    basic: i18n.t("finance.planSelector.values.unlimited"),
    advance: i18n.t("finance.planSelector.values.unlimited"),
    premium: i18n.t("finance.planSelector.values.unlimited"),
  },
  {
    name: i18n.t("finance.planSelector.feats.orderForms"),
    iconKey: "form",
    iconColor: "#14b8a6",
    free: i18n.t("finance.planSelector.values.included"),
    basic: i18n.t("finance.planSelector.values.unlimited"),
    advance: i18n.t("finance.planSelector.values.unlimited"),
    premium: i18n.t("finance.planSelector.values.unlimited"),
  },
  {
    type: "category",
    name: i18n.t("finance.planSelector.categories.publications"),
    iconKey: "posts",
    color: "#0891b2",
    bgColor: "#cffafe",
  },
  {
    name: i18n.t("finance.planSelector.feats.waStatusPost"),
    iconKey: "status",
    iconColor: "#22c55e",
    free: i18n.t("finance.planSelector.values.no"),
    basic: i18n.t("finance.planSelector.values.perMonth", { count: 60 }),
    advance: i18n.t("finance.planSelector.values.perMonth", { count: 150 }),
    premium: i18n.t("finance.planSelector.values.unlimited"),
  },
  {
    name: i18n.t("finance.planSelector.feats.waGroupsPost"),
    iconKey: "group",
    iconColor: "#0ea5e9",
    free: i18n.t("finance.planSelector.values.no"),
    basic: i18n.t("finance.planSelector.values.perMonth", { count: "5,000" }),
    advance: i18n.t("finance.planSelector.values.perMonth", { count: "20,000" }),
    premium: i18n.t("finance.planSelector.values.unlimited"),
  },
  {
    name: i18n.t("finance.planSelector.feats.waCommunities"),
    iconKey: "community",
    iconColor: "#6366f1",
    free: i18n.t("finance.planSelector.values.no"),
    basic: i18n.t("finance.planSelector.values.perMonth", { count: "5,000" }),
    advance: i18n.t("finance.planSelector.values.perMonth", { count: "20,000" }),
    premium: i18n.t("finance.planSelector.values.unlimited"),
  },
  {
    type: "category",
    name: i18n.t("finance.planSelector.categories.operations"),
    iconKey: "operations",
    color: "#4f46e5",
    bgColor: "#e0e7ff",
  },
  {
    name: i18n.t("finance.planSelector.feats.orderNotification"),
    iconKey: "bell",
    iconColor: "#10b981",
    free: i18n.t("finance.planSelector.values.yes"),
    basic: i18n.t("finance.planSelector.values.yes"),
    advance: i18n.t("finance.planSelector.values.yes"),
    premium: i18n.t("finance.planSelector.values.yes"),
  },
  {
    name: i18n.t("finance.planSelector.feats.internalAlerts"),
    iconKey: "chat",
    iconColor: "#f59e0b",
    free: i18n.t("finance.planSelector.values.no"),
    basic: i18n.t("finance.planSelector.values.unlimited"),
    advance: i18n.t("finance.planSelector.values.unlimited"),
    premium: i18n.t("finance.planSelector.values.unlimited"),
  },
  {
    name: i18n.t("finance.planSelector.feats.webPlugin"),
    iconKey: "widget",
    iconColor: "#0ea5e9",
    free: i18n.t("finance.planSelector.values.no"),
    basic: "50",
    advance: "250",
    premium: i18n.t("finance.planSelector.values.unlimited"),
  },
  {
    type: "category",
    name: i18n.t("finance.planSelector.categories.other"),
    iconKey: "other",
    color: "#4b5563",
    bgColor: "#f3f4f6",
  },
  {
    name: i18n.t("finance.planSelector.feats.multiLang"),
    iconKey: "lang",
    iconColor: "#3b82f6",
    free: i18n.t("finance.planSelector.values.included"),
    basic: i18n.t("finance.planSelector.values.included"),
    advance: i18n.t("finance.planSelector.values.included"),
    premium: i18n.t("finance.planSelector.values.included"),
  },
  {
    name: i18n.t("finance.planSelector.feats.techSupport"),
    iconKey: "headset",
    iconColor: "#10b981",
    free: i18n.t("finance.planSelector.values.waSupport"),
    basic: i18n.t("finance.planSelector.values.waPrioritySupport"),
    advance: i18n.t("finance.planSelector.values.waPrioritySupport"),
    premium: i18n.t("finance.planSelector.values.waPrioritySupport"),
  },
];

const PlanSelectorModal = ({ open, onClose }) => {
  const classes = useStyles();
  const { getPlanList } = usePlans();
  const { user } = useContext(AuthContext);
  const { getAll: getAllSettings } = useSettings();

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState("mensual"); // "mensual" | "trimestral"
  const [currency, setCurrency] = useState("$");
  const [processingId, setProcessingId] = useState(null);

  const matrixRows = useMemo(() => getMatrixRows(), [i18n.language]);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        if (user && user.companyId) {
          const settings = await getAllSettings(user.companyId);
          const systemCurrency = settings.find((s) => s.key === "system_currency");
          if (systemCurrency && systemCurrency.value) {
            setCurrency(systemCurrency.value);
          }
        }
      } catch (error) {
        console.error("Error fetching system currency:", error);
      }
    };

    const fetchPlans = async () => {
      setLoading(true);
      try {
        const data = await getPlanList();
        setPlans(data || []);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      fetchSettings();
      fetchPlans();
    }
  }, [open, user]);

  const currentPlanId = user?.company?.planId;
  const currentPlanObj = plans.find((p) => p.id === currentPlanId);
  const currentPlanName = (currentPlanObj?.name || user?.company?.plan?.name || "").toUpperCase();

  const isCurrentFreemium = currentPlanId === 10 || currentPlanName.includes("FREEMIUM");
  const isCurrentBasic = currentPlanId === 2 || currentPlanId === 3 || currentPlanName.startsWith("BA") || currentPlanName.includes("BASIC");
  const isCurrentAdvance = currentPlanId === 6 || currentPlanId === 7 || currentPlanName.startsWith("AD") || currentPlanName.includes("ADVANCE");
  const isCurrentPremium = currentPlanId === 11 || currentPlanId === 12 || currentPlanName.startsWith("PR") || currentPlanName.includes("PREMIUM");

  // Resolver ID de planes según el período seleccionado
  const basicPlan =
    period === "mensual"
      ? plans.find((p) => p.name?.toUpperCase().startsWith("BA1")) || plans.find((p) => p.id === 2)
      : plans.find((p) => p.name?.toUpperCase().startsWith("BA2")) || plans.find((p) => p.id === 3);

  const advancePlan =
    period === "mensual"
      ? plans.find((p) => p.name?.toUpperCase().startsWith("AD1")) || plans.find((p) => p.id === 6)
      : plans.find((p) => p.name?.toUpperCase().startsWith("AD2")) || plans.find((p) => p.id === 7);

  const premiumPlan =
    period === "mensual"
      ? plans.find((p) => p.name?.toUpperCase().startsWith("PR1")) || plans.find((p) => p.id === 11)
      : plans.find((p) => p.name?.toUpperCase().startsWith("PR2")) || plans.find((p) => p.id === 12);

  const handleSelectPlan = async (targetPlan) => {
    if (!targetPlan || !targetPlan.id) return;
    setProcessingId(targetPlan.id);
    try {
      const { data } = await api.post("/openpay/plan", { planId: targetPlan.id });
      if (data && data.link) {
        window.open(data.link, "_blank");
        onClose();
      }
    } catch (err) {
      toastError(err);
    } finally {
      setProcessingId(null);
    }
  };

  const renderValue = (val) => {
    if (!val) return "-";
    const yesText = i18n.t("finance.planSelector.values.yes");
    const noText = i18n.t("finance.planSelector.values.no");
    const unlimText = i18n.t("finance.planSelector.values.unlimited");
    const inclText = i18n.t("finance.planSelector.values.included");

    if (val === yesText || val === "Sí" || val === "Yes" || val === "Sim" || val === "Oui" || val === "نعم") {
      return <span className={classes.valYes}>{val}</span>;
    }
    if (val === noText || val === "No" || val === "Não" || val === "Non" || val === "لا") {
      return <span className={classes.valNo}>{val}</span>;
    }
    if (
      val.includes(unlimText) ||
      val.includes(inclText) ||
      val.includes("Ilimitado") ||
      val.includes("Incluido") ||
      val.includes("Unlimited") ||
      val.includes("Included") ||
      val.includes("Illimité") ||
      val.includes("Inclus") ||
      val.includes("غير محدود") ||
      val.includes("مشمول")
    ) {
      return <span className={classes.valUnlimited}>{val}</span>;
    }
    return <span className={classes.valNum}>{val}</span>;
  };

  const renderCtaButton = (tierKey, targetPlan, isCurrentTier, buttonClass) => {
    const isInactive = targetPlan && targetPlan.isPublic === false;

    if (isCurrentTier) {
      return (
        <Button
          fullWidth
          variant="outlined"
          disabled
          className={classes.btnCurrent}
          startIcon={<CheckIcon />}
        >
          {i18n.t("finance.planSelector.currentPlanBadge")}
        </Button>
      );
    }

    return (
      <Button
        fullWidth
        variant="contained"
        className={buttonClass}
        disabled={processingId === targetPlan?.id || isInactive || !targetPlan}
        onClick={() => handleSelectPlan(targetPlan)}
      >
        {processingId === targetPlan?.id ? (
          <CircularProgress size={20} style={{ color: "#ffffff" }} />
        ) : isInactive ? (
          i18n.t("finance.planSelector.inactive")
        ) : (
          i18n.t("finance.planSelector.acquirePlan")
        )}
      </Button>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      scroll="paper"
      classes={{ paper: classes.dialogPaper }}
    >
      <div className={classes.headerBox}>
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
          size="small"
        >
          <CloseIcon />
        </IconButton>

        <div className={classes.pillBadge}>
          <StarIcon style={{ fontSize: "1rem" }} />
          <span>{i18n.t("finance.planSelector.ourPrices")}</span>
          <StarIcon style={{ fontSize: "1rem" }} />
        </div>

        <Typography className={classes.mainTitle}>
          {i18n.t("finance.planSelector.simpleTransparent")}
        </Typography>

        <Typography className={classes.subtitleText}>
          {i18n.t("finance.planSelector.subtitle")}
        </Typography>

        {/* PRICING TOGGLE SWITCHER */}
        <div className={classes.switcherContainer}>
          <span
            className={`${classes.switcherLabel} ${
              period === "mensual" ? classes.switcherLabelActive : ""
            }`}
            onClick={() => setPeriod("mensual")}
          >
            {i18n.t("finance.planSelector.monthly")}
          </span>
          <Switch
            checked={period === "trimestral"}
            onChange={(e) => setPeriod(e.target.checked ? "trimestral" : "mensual")}
            color="primary"
          />
          <span
            className={`${classes.switcherLabel} ${
              period === "trimestral" ? classes.switcherLabelActive : ""
            }`}
            onClick={() => setPeriod("trimestral")}
          >
            {i18n.t("finance.planSelector.quarterly")}
            <span className={classes.discountBadge}>
              {i18n.t("finance.planSelector.save25")}
            </span>
          </span>
        </div>
      </div>

      <DialogContent dividers className={classes.tableContainer}>
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "60px 0" }}>
            <CircularProgress />
          </div>
        ) : (
          <table className={classes.table}>
            <thead>
              <tr>
                {/* Features Header */}
                <th
                  className={classes.thFeat}
                  style={{ width: isCurrentFreemium ? "28%" : "31%" }}
                >
                  <Typography variant="h6" align="center" style={{ fontWeight: 800 }}>
                    {i18n.t("finance.planSelector.features")}
                  </Typography>
                </th>

                {/* Freemium (Solo visible si el cliente actualmente tiene Freemium) */}
                {isCurrentFreemium && (
                  <th
                    className={`${classes.thPlan} ${classes.thFree}`}
                    style={{ width: "18%" }}
                  >
                    <div className={classes.planTitle}>Freemium</div>
                    <div className={classes.planPrice}>
                      {currency} 0{" "}
                      <span className={classes.planPeriod}>
                        {period === "mensual"
                          ? i18n.t("finance.planSelector.perMonthSuffix")
                          : i18n.t("finance.planSelector.perQuarterSuffix")}
                      </span>
                    </div>
                    <div className={classes.planSubtitle}>{i18n.t("finance.planSelector.descFreemium")}</div>
                    <Button fullWidth variant="outlined" disabled className={classes.btnCurrent}>
                      {i18n.t("finance.planSelector.currentPlanBadge")}
                    </Button>
                  </th>
                )}

                {/* Basic */}
                <th
                  className={`${classes.thPlan} ${classes.thBasic}`}
                  style={{ width: isCurrentFreemium ? "18%" : "23%" }}
                >
                  <div className={classes.planTitle}>Basic</div>
                  <div className={classes.planPrice}>
                    {currency} {period === "mensual" ? "20" : "45"}{" "}
                    <span className={classes.planPeriod}>
                      {period === "mensual"
                        ? i18n.t("finance.planSelector.perMonthSuffix")
                        : i18n.t("finance.planSelector.perQuarterSuffix")}
                    </span>
                  </div>
                  <div className={classes.planSubtitle}>{i18n.t("finance.planSelector.descBasic")}</div>
                  {renderCtaButton("basic", basicPlan, isCurrentBasic, classes.btnBasic)}
                </th>

                {/* Advance */}
                <th
                  className={`${classes.thPlan} ${classes.thAdvance}`}
                  style={{ width: isCurrentFreemium ? "18%" : "23%" }}
                >
                  <div className={classes.popularBadge}>
                    {i18n.t("finance.planSelector.mostPopular")}
                  </div>
                  <div className={classes.planTitle}>Advance</div>
                  <div className={classes.planPrice}>
                    {currency} {period === "mensual" ? "47" : "105"}{" "}
                    <span className={classes.planPeriod}>
                      {period === "mensual"
                        ? i18n.t("finance.planSelector.perMonthSuffix")
                        : i18n.t("finance.planSelector.perQuarterSuffix")}
                    </span>
                  </div>
                  <div className={classes.planSubtitle}>{i18n.t("finance.planSelector.descAdvance")}</div>
                  {renderCtaButton("advance", advancePlan, isCurrentAdvance, classes.btnAdvance)}
                </th>

                {/* Premium */}
                <th
                  className={`${classes.thPlan} ${classes.thPremium}`}
                  style={{ width: isCurrentFreemium ? "18%" : "23%" }}
                >
                  <div className={classes.planTitle}>Premium</div>
                  <div className={classes.planPrice}>
                    {currency} {period === "mensual" ? "97" : "200.86"}{" "}
                    <span className={classes.planPeriod}>
                      {period === "mensual"
                        ? i18n.t("finance.planSelector.perMonthSuffix")
                        : i18n.t("finance.planSelector.perQuarterSuffix")}
                    </span>
                  </div>
                  <div className={classes.planSubtitle}>{i18n.t("finance.planSelector.descPremium")}</div>
                  {renderCtaButton("premium", premiumPlan, isCurrentPremium, classes.btnPremium)}
                </th>
              </tr>
            </thead>

            <tbody>
              {matrixRows.map((row, idx) => {
                if (row.type === "category") {
                  return (
                    <tr key={`cat-${idx}`}>
                      <td className={classes.catRowFeat}>
                        <div
                          className={classes.catLabel}
                          style={{ backgroundColor: row.bgColor, color: row.color }}
                        >
                          {getCategoryIcon(row.iconKey)}
                          {row.name}
                        </div>
                      </td>
                      {isCurrentFreemium && <td className={classes.catEmptyFree} />}
                      <td className={classes.catEmptyBasic} />
                      <td className={classes.catEmptyAdv} />
                      <td className={classes.catEmptyPrem} />
                    </tr>
                  );
                }

                return (
                  <tr key={`feat-${idx}`}>
                    <td className={classes.tdFeat}>
                      {getFeatureIcon(row.iconKey, row.iconColor)}
                      <span style={{ flex: 1 }}>{row.name}</span>
                      {row.tooltip && (
                        <Tooltip title={row.tooltip} arrow placement="right">
                          <HelpOutlineIcon
                            style={{
                              fontSize: "0.95rem",
                              marginLeft: 6,
                              color: "#94a3b8",
                              cursor: "help",
                            }}
                          />
                        </Tooltip>
                      )}
                    </td>
                    {isCurrentFreemium && (
                      <td className={classes.tdFree}>{renderValue(row.free)}</td>
                    )}
                    <td className={classes.tdBasic}>{renderValue(row.basic)}</td>
                    <td className={classes.tdAdv}>{renderValue(row.advance)}</td>
                    <td className={classes.tdPrem}>{renderValue(row.premium)}</td>
                  </tr>
                );
              })}

              {/* Bottom CTA row */}
              <tr>
                <td className={classes.tdFeatBottom} />
                {isCurrentFreemium && (
                  <td className={classes.tdFreeBottom}>
                    <Button fullWidth variant="outlined" disabled className={classes.btnCurrent}>
                      {i18n.t("finance.planSelector.currentPlanBadge")}
                    </Button>
                  </td>
                )}
                <td className={classes.tdBasicBottom}>
                  {renderCtaButton("basic", basicPlan, isCurrentBasic, classes.btnBasic)}
                </td>
                <td className={classes.tdAdvBottom}>
                  {renderCtaButton("advance", advancePlan, isCurrentAdvance, classes.btnAdvance)}
                </td>
                <td className={classes.tdPremBottom}>
                  {renderCtaButton("premium", premiumPlan, isCurrentPremium, classes.btnPremium)}
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PlanSelectorModal;
